import { CurrencyDisplayUtil } from './../../common/currency-display-util';
import { SnapshotService } from './../../snapshot/snapshot.service';
import { AwsUtil } from './../../authentication/aws.service';
import {
    Transaction,
    WebApiEndpoints,
    generateEventDatetime,
    CurrencyUtil,
    BusinessAlertsRequest,
    BusinessAlerts,
    PaymentRelease,
    convertFromConstantToHuman
} from '@liquitics/transaction';
import { AppConfig } from './../../app.config';
import { Subject, Observable, ReplaySubject, BehaviorSubject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { AlertMessage } from './alert-message';
import { BaseRestService } from '../../service/base-rest.service';
import { DashboardEventService } from './../../dashboard/dashboard-event.service';
import { CognitoUtil } from './../../authentication/cognito.service';
import { AppEnvironment } from './../../app-env';
import { environment } from '../../../environments/environment';
import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { BasePollingRestService } from '../../service/base-polling-rest.service';
import { PollableService } from '../../service/pollable-service';
import { ServiceRegistry } from '../../service/service-registry.service';
import { MatchingStatusReport, MatchExpectation } from '@liquitics/matching';
import * as moment from 'moment';
import { AlertRow, toAlertType } from './alert-row';
import { productTypeDisplayName } from '../../common/product-types';

@Injectable()
export class AlertMessageService extends BasePollingRestService<BusinessAlerts> {
    private POLLING_INTERVAL = 30000;

    private dataSubject$: Subject<BusinessAlerts>;

    private alertRowSubscription$: Observable<AlertRow[]>;
    private alertRowSubject$: BehaviorSubject<AlertRow[]>;
    private utcOffset: string;

    constructor(protected http: HttpClient,
        protected awsUtil: AwsUtil,
        protected serviceRegistry: ServiceRegistry,
        protected dashboardEventService: DashboardEventService,
        private snapshotService: SnapshotService,
        private datePipe: DatePipe,
        private appConfig: AppConfig) {

        super(http, awsUtil, serviceRegistry, dashboardEventService);
        this.createPollingDataSubject(this.POLLING_INTERVAL);
        this.dataSubject$ = this.getPollingDataSubject() as Subject<BusinessAlerts>;
        this.utcOffset = appConfig.getTenantInfo().utcOffset;
        this.throwExecptionOnError = true;
    }

    registerService() {
        this.createAlertRowStream();
        this.serviceRegistry.registerService(this);
    }

    getDataSubject() {
        return this.dataSubject$;
    }

    getAlertRowSubject(): BehaviorSubject<AlertRow[]> {
        return this.alertRowSubject$;
    }

    startPolling() {
        super.startPolling();
        this.alertRowSubscription$
            .subscribe({
                next: (rows: AlertRow[]) => {
                    this.alertRowSubject$.next(rows);
                },
                error: (err) => {
                    this.alertRowSubject$.error(err);
                }
            });
    }


    // protected makeAPICall(authToken: string, params?: any): Observable<MatchingStatusReport> {
    //     const options = { headers: this.requestHeaders(authToken) };

    //     const body = {
    //         'fromTime': moment().utcOffset(this.appConfig.getTenantInfo().utcOffset).startOf('day').toISOString(),
    //         'toTime': moment().toISOString(),
    //         // 'toTime': moment().add(16,'hours').toISOString(),//TEMPTEMP
    //         'expectationTypes': ['release', 'confirmation'],
    //         'detailLevel': 'transaction',
    //         'detailFields': ['amount', 'account', 'client', 'currency', 'desk', 'externalId', 'productType']
    //     };

    //     return this.http.post<MatchingStatusReport>(this.getAPIName(params), body, options);
    // }


    buildPollingRequestParams() {
        const businessAlertRequest = new BusinessAlertsRequest();
        businessAlertRequest.matchingFromTime = this.appConfig.getStartOfDayDatetimeUTC().toDate();
        businessAlertRequest.matchingToTime = moment.utc().toDate();
        businessAlertRequest.matchingExpectationTypes = ['release', 'confirmation'];
        businessAlertRequest.matchingDetailLevel = 'transaction';
        businessAlertRequest.matchingDetailFields = ['amount', 'account', 'client', 'currency', 'desk',
            'externalId', 'productType', 'flowKey', 'eventDatetime'];
        const requestAny: any = businessAlertRequest;
        requestAny.matchingFromTime = generateEventDatetime(moment().utcOffset(this.appConfig.getTenantInfo().utcOffset).startOf('day'));
        requestAny.matchingToTime = generateEventDatetime(moment());

        return businessAlertRequest;
    }

    getAPIName(params?: any): string {
        return WebApiEndpoints.QueryBusinessAlerts;
    }

    useDemoDataFile(): boolean {
        return false;
    }

    private createAlertRowStream() {
        this.alertRowSubject$ = new BehaviorSubject<AlertRow[]>([]);
        this.alertRowSubscription$ = this.getPollingDataSubject()
            .pipe(map((businessAlerts: BusinessAlerts) => {
                const alertRows: AlertRow[] = [];
                const matchingStatusReport: MatchingStatusReport = businessAlerts.matchingStatusReport;
                const now = moment();
                if (matchingStatusReport && matchingStatusReport.matchExpectations) {
                    const accountNames = this.snapshotService.applicationConfiguration.accountNames;
                    alertRows.push(...matchingStatusReport.matchExpectations
                        .map((expectation: MatchExpectation) => {
                            const expectedTime = this.appConfig.getStartOfDayDatetimeUTC().clone()
                                .add(parseInt(expectation.expectedTime, 10), 'minutes');
                            const row = {
                                matchExpectation: expectation,
                                expectedTimeMoment: expectedTime,
                            } as AlertRow;
                            return row;
                        })
                        .map((row: AlertRow) => {
                            const transaction: Transaction =
                                matchingStatusReport.flowKeyIndexToTransaction[row.matchExpectation.flowKeyIndex];
                            this.decorateTransaction(transaction);
                            row.transaction = transaction;
                            row.scaledAmount = CurrencyDisplayUtil.toShortNumber(transaction.amount, 2);
                            // row.scaledAmount = CurrencyUtil.roundAndScale(transaction.amount, 2,
                            //     CurrencyUtil.currencyScalePower(transaction.currency));
                            const minutesLate = Math.abs(row.expectedTimeMoment.diff(now, 'minutes'));
                            row.minutesLate = minutesLate;
                            row.description = this.toMissedConfirmationDescription(transaction);
                            row.icon = 'hourglass_empty';
                            row.type = toAlertType(row);
                            row.typeDisplay = convertFromConstantToHuman(row.type);
                            if (accountNames[row.transaction.account]) {
                                row.transaction.account = accountNames[row.transaction.account];
                            }
                            return row;
                        })
                    );
                }
                if (businessAlerts.unreleasedPayments) {
                    alertRows.push(...businessAlerts.unreleasedPayments
                        .map((unreleasedPayment: PaymentRelease) => {
                            const row = {
                                expectedTimeMoment: moment.utc(unreleasedPayment.expectedSettleTime),
                                icon: 'money_off',
                                description: unreleasedPayment.account + '(' + unreleasedPayment.desk + ')',
                                type: 'INSUFFICIENT_FUNDS'
                            } as AlertRow;
                            row.typeDisplay = convertFromConstantToHuman(row.type);
                            const minutesLate = Math.abs(row.expectedTimeMoment.diff(now, 'minutes'));
                            row.minutesLate = minutesLate;
                            row.scaledAmount = CurrencyDisplayUtil.toShortNumber(unreleasedPayment.amount, 2);
                            const partialTxn = new Transaction();
                            partialTxn.account = unreleasedPayment.account;
                            partialTxn.amount = unreleasedPayment.amount;
                            partialTxn.externalId = unreleasedPayment.externalId;
                            partialTxn.source = unreleasedPayment.source;
                            partialTxn.settleDate = unreleasedPayment.settleDate;
                            partialTxn.desk = unreleasedPayment.desk;
                            partialTxn.currency = unreleasedPayment.currency;
                            partialTxn.client = unreleasedPayment.client;
                            partialTxn.productType = unreleasedPayment.productType;
                            partialTxn.flowKey = unreleasedPayment.flowKey;
                            partialTxn.eventDatetime = unreleasedPayment.eventDatetime;
                            row.transaction = partialTxn
                            return row;
                        })
                    );
                }
                return alertRows;
            }));
    }

    private decorateTransaction(transaction: Transaction) {
        if (transaction) {
            if (transaction.productType) {
                transaction.productType = productTypeDisplayName(transaction.productType);
            }
        }
    }

    private toMissedConfirmationDescription(transaction: Transaction) {
        return transaction.client + '(' + transaction.desk + ')';
    }


}
